import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Trash2, Plus, Inbox, Lock, LogOut } from "lucide-react";
import { api, ADMIN_TOKEN_KEY } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const emptyArticle = {
  title: "", subtitle: "", author: "Lead ITES Editorial", publication: "Lead ITES",
  category: "Technology", excerpt: "", body: "", cover_image: "", featured: false,
};

const Admin = () => {
  const [authed, setAuthed] = useState(null); // null=checking, false=login, true=in
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(emptyArticle);
  const [topic, setTopic] = useState("");
  const [genCat, setGenCat] = useState("Technology");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subs, setSubs] = useState({ leads: [], quotes: [], partnerships: [], contacts: [] });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = () => {
    api.get("/articles", { params: { limit: 100 } }).then((r) => setArticles(r.data));
    api.get("/admin/submissions").then((r) => setSubs(r.data));
  };

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    api.get("/auth/me").then(() => { setAuthed(true); load(); })
      .catch(() => { localStorage.removeItem(ADMIN_TOKEN_KEY); setAuthed(false); });
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (!password) { toast.error("Enter the admin password."); return; }
    setLoggingIn(true);
    try {
      const { data } = await api.post("/auth/login", { password });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      setPassword("");
      setAuthed(true);
      load();
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthed(false);
  };

  const generate = async () => {
    if (!topic.trim()) { toast.error("Enter a topic to generate."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/generate-article", { topic, category: genCat });
      setForm((f) => ({
        ...f, title: data.title || f.title, subtitle: data.subtitle || "",
        excerpt: data.excerpt || "", body: data.body || "", category: data.category || genCat,
      }));
      toast.success("Draft generated. Review and publish below.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) { toast.error("Title and body are required."); return; }
    setSaving(true);
    try {
      await api.post("/articles", form);
      toast.success("Article published.");
      setForm(emptyArticle);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/articles/${id}`);
      toast.success("Article deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const subTable = (rows, cols) => (
    rows.length === 0 ? (
      <div className="py-12 text-center text-muted-foreground flex flex-col items-center gap-2">
        <Inbox className="h-6 w-6" /> No submissions yet.
      </div>
    ) : (
      <div className="border border-border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>{cols.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                {cols.map((c) => <TableCell key={c.key} className="text-sm">{r[c.key] || "-"}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  );

  if (authed === null) {
    return <div className="py-32 flex justify-center" data-testid="admin-loading"><Loader2 className="h-6 w-6 animate-spin text-electric" /></div>;
  }

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-5" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-border bg-white p-8 shadow-sm">
          <div className="h-12 w-12 rounded-xl bg-navy flex items-center justify-center">
            <Lock className="h-6 w-6 text-electric" />
          </div>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-navy">Admin access</h1>
          <p className="mt-2 text-sm text-muted-foreground">Enter the admin password to manage content and view submissions.</p>
          <div className="mt-6 space-y-1.5">
            <Label htmlFor="admin-pass">Password</Label>
            <Input id="admin-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" data-testid="admin-password" />
          </div>
          <Button type="submit" disabled={loggingIn} className="btn-electric rounded-full w-full mt-5" data-testid="admin-login-submit">
            {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-5 lg:px-8 py-12" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <span className="overline-label text-electric">Admin</span>
          <h1 className="mt-2 font-display text-3xl font-extrabold text-navy">Content & Lead Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-secondary text-navy hover:bg-secondary">{articles.length} articles · {subs.leads.length} leads</Badge>
          <Button variant="outline" size="sm" onClick={logout} className="rounded-full" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Logout
          </Button>
        </div>
      </div>

      <Tabs defaultValue="content" className="mt-8">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="tab-submissions">Submissions</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-6 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-5">
            {/* AI generator */}
            <div className="rounded-xl border border-border bg-secondary/50 p-6" data-testid="ai-generator">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-electric" />
                <h2 className="font-display font-bold text-navy">AI Content Generation</h2>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">Draft an original article with AI, then review before publishing.</p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. The future of edge AI in manufacturing" data-testid="gen-topic" />
                <Select value={genCat} onValueChange={setGenCat}>
                  <SelectTrigger className="sm:w-56" data-testid="gen-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={generate} disabled={generating} className="btn-electric rounded-full shrink-0" data-testid="gen-button">
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" /> Generate</>}
                </Button>
              </div>
            </div>

            {/* Create form */}
            <form onSubmit={save} className="rounded-xl border border-border bg-white p-6 space-y-4" data-testid="article-form">
              <h2 className="font-display font-bold text-navy flex items-center gap-2"><Plus className="h-4 w-4" /> Publish article</h2>
              <div className="space-y-1.5"><Label>Title *</Label><Input value={form.title} onChange={set("title")} data-testid="art-title" /></div>
              <div className="space-y-1.5"><Label>Subtitle</Label><Input value={form.subtitle} onChange={set("subtitle")} data-testid="art-subtitle" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Author</Label><Input value={form.author} onChange={set("author")} data-testid="art-author" /></div>
                <div className="space-y-1.5"><Label>Publication</Label><Input value={form.publication} onChange={set("publication")} data-testid="art-publication" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="art-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Cover image URL</Label><Input value={form.cover_image} onChange={set("cover_image")} data-testid="art-cover" /></div>
              </div>
              <div className="space-y-1.5"><Label>Excerpt</Label><Textarea rows={2} value={form.excerpt} onChange={set("excerpt")} data-testid="art-excerpt" /></div>
              <div className="space-y-1.5"><Label>Body (HTML) *</Label><Textarea rows={10} value={form.body} onChange={set("body")} className="font-mono-plex text-xs" data-testid="art-body" /></div>
              <label className="flex items-center gap-2 text-sm text-navy">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} data-testid="art-featured" />
                Feature on homepage
              </label>
              <Button type="submit" disabled={saving} className="btn-electric rounded-full" data-testid="art-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish Article"}
              </Button>
            </form>
          </div>

          {/* Article list */}
          <div className="lg:col-span-2">
            <h2 className="font-display font-bold text-navy mb-4">Published ({articles.length})</h2>
            <div className="space-y-3 max-h-[800px] overflow-y-auto pr-1">
              {articles.map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-3 border border-border rounded-lg p-4 bg-white" data-testid={`admin-article-${a.slug}`}>
                  <div>
                    <p className="text-sm font-semibold text-navy leading-snug">{a.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{a.category} · {a.author}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(a.id)} className="text-destructive shrink-0" data-testid={`delete-${a.slug}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="submissions" className="mt-6 space-y-10">
          <div>
            <h2 className="font-display font-bold text-navy mb-4">Leads ({subs.leads.length})</h2>
            {subTable(subs.leads, [
              { key: "name", label: "Name" }, { key: "company", label: "Company" },
              { key: "email", label: "Email" }, { key: "source", label: "Source" },
              { key: "report_slug", label: "Report" },
            ])}
          </div>
          <div>
            <h2 className="font-display font-bold text-navy mb-4">Quote Requests ({subs.quotes.length})</h2>
            {subTable(subs.quotes, [
              { key: "name", label: "Name" }, { key: "company", label: "Company" },
              { key: "solution", label: "Solution" }, { key: "budget", label: "Budget" },
              { key: "email", label: "Email" },
            ])}
          </div>
          <div>
            <h2 className="font-display font-bold text-navy mb-4">Partnerships ({subs.partnerships.length})</h2>
            {subTable(subs.partnerships, [
              { key: "name", label: "Name" }, { key: "company", label: "Company" },
              { key: "partnership_type", label: "Type" }, { key: "email", label: "Email" },
            ])}
          </div>
          <div>
            <h2 className="font-display font-bold text-navy mb-4">Contact Messages ({subs.contacts.length})</h2>
            {subTable(subs.contacts, [
              { key: "name", label: "Name" }, { key: "subject", label: "Subject" },
              { key: "email", label: "Email" },
            ])}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Admin;
